import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, LayoutGrid, Users, TrendingUp,
  Settings, LogOut, Store, X, ClipboardList, Tag, MessageSquare,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearAuth } from '../store/authSlice';
import { api } from '../api/client';
import { version } from '../../package.json';

const BASE_MENU = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/products', label: 'Productos', icon: ShoppingBag, end: false },
  { to: '/orders', label: 'Pedidos', icon: MessageSquare, end: false },
  { to: '/categories', label: 'Categorías', icon: LayoutGrid, end: false },
  { to: '/promotions', label: 'Promociones', icon: Tag, end: false },
  { to: '/customers', label: 'Clientes', icon: Users, end: false },
  { to: '/analytics', label: 'Analíticas', icon: TrendingUp, end: false },
];

const SUPER_MENU = [
  { to: '/logs', label: 'Actividad', icon: ClipboardList, end: false },
];

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
    ? 'bg-brand-primary text-white shadow-sm font-bold'
    : 'text-ink-muted hover:bg-page-surface hover:text-ink'
  }`;

interface SidebarContentProps {
  onClose?: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const menuItems = user?.role === 'superadmin' ? SUPER_MENU : BASE_MENU;

  const handleLogout = async () => {
    try { await api.post('/api/v1/auth/logout', {}); } catch { /* ignore */ }
    dispatch(clearAuth());
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-page-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-brand-primary p-2.5 rounded-xl shadow-sm shadow-brand-glow">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-lg leading-none text-ink tracking-wide">Aye Otero</h1>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-[10px] text-brand-primary font-semibold tracking-wider uppercase">Panel Control</span>
              <span className="text-[9px] text-ink-faint font-medium bg-page-surface border border-page-border px-1 py-0.5 rounded">v{version}</span>
            </div>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1.5 rounded-lg hover:bg-page-surface transition-colors lg:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={linkCls}
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-ink-muted'}`} />
                  {item.label}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-page-border space-y-1">
        {user && (
          <div className="px-4 py-2 mb-1">
            <p className="text-xs font-semibold text-ink truncate">{user.name}</p>
            <p className="text-[10px] text-ink-muted truncate">{user.email}</p>
            {user.role === 'superadmin' && (
              <span className="text-[9px] font-bold uppercase tracking-wider text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded border border-violet-200 mt-1.5 inline-block">
                Superadmin
              </span>
            )}
          </div>
        )}
        <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs font-medium text-ink-muted hover:bg-page-surface hover:text-ink transition-all">
          <Settings className="w-3.5 h-3.5" />
          Configuración
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs font-medium text-rose-600 hover:bg-rose-50 hover:text-rose-700 transition-all"
        >
          <LogOut className="w-3.5 h-3.5" />
          Cerrar Sesión
        </button>
      </div>
    </div>
  );
};

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => (
  <>
    {/* Desktop — fixed left */}
    <aside className="hidden lg:flex flex-col w-64 h-screen fixed left-0 top-0 bg-white border-r border-page-border z-50! shadow-sm">
      <SidebarContent />
    </aside>

    {/* Mobile drawer */}
    {mobileOpen && (
      <div className="fixed inset-0 z-[60] lg:hidden animate-fade-in">
        <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={onMobileClose} />
        <aside className="absolute left-0 top-0 h-full w-72 bg-white border-r border-page-border flex flex-col animate-slide-in-left shadow-xl">
          <SidebarContent onClose={onMobileClose} />
        </aside>
      </div>
    )}
  </>
);
